import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AppStateService } from '../../services/app-state.service';
import { WallpaperService } from '../../services/wallpaper.service';
import { BaseAPIService } from 'src/app/services/base-api.service';
import { DataLayerService } from '../../services/datalayer.service';
import { AuthService } from '../../services/auth.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-art-ready',
  templateUrl: './art-ready.component.html'
})
export class ArtReadyComponent implements OnInit, OnDestroy {

  products: any[] = [];
  isLoading = true;
  loadError = false;
  totalRecords = 0;
  showFilterDrawer = false;

  // ── FILTER DATA ─────────────────────────
  collections = [];
  artTypes:any = [];
  spaces = [];
  sizes = [];
  orientations:any = [];
  productTypes:any= [];

  frameStyles = [];
  typeOfFrames = [];
  discounts = [];

  facet: any = {};

  // ── SELECTED FILTERS ───────────────────
  selectedCollections: any[] = [];
  selectedArtTypes: any[] = [];
  selectedSpaces: any[] = [];
  selectedSizes: any[] = [];
  selectedOrientations: any[] = [];
  selectedProductTypes: any[] = [];
  selectedFrameStyles: any[] = [];
  selectedTypeOfFrames: any[] = [];
  selectedDiscounts: any[] = [];

  currentPage = 1;
  readonly pageSize = 12;

  sortBy = 'Newest';
  sortOptions = ['Relevance', 'Popular', 'Newest', 'Price: Low → High', 'Price: High → Low'];
  showSortDropdown = false;

  sortby = 'created_at';
  sortDir = 'desc';

  wishlistIds = new Set<string>();
  private sub = new Subscription();

  private apiUrl = environment.apiUrl;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    public state: AppStateService,
    private cdr: ChangeDetectorRef,
    private wallpaperService: WallpaperService,
    private baseApiService: BaseAPIService,
    private dataLayer: DataLayerService,
    private http: HttpClient,
    private authService: AuthService,
  ) { }

  private get sessionId(): string {
    let sid = localStorage.getItem('session_id');
    if (!sid) {
      sid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = Math.random() * 16 | 0;
        return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
      });
      localStorage.setItem('session_id', sid);
    }
    return sid;
  }

  private get authHeaders() {
    const token = localStorage.getItem('auth_token');
    const h: any = { 'Content-Type': 'application/json' };
    if (token) h['Authorization'] = `Bearer ${token}`;
    return { headers: h };
  }

  ngOnInit() {
    this.sub.add(this.state.wishlistIds$.subscribe(ids => this.wishlistIds = ids));

    this.productTypes = [
      { name: 'Art Prints', slug: 'art_print', activated: false },
      { name: 'Canvas Art', slug: 'canvas', activated: false },
      { name: 'Framed Art', slug: 'framing', activated: false }
    ];

    this.artTypes = [
      { id: 1, name: 'Painting', activated: false },
      { id: 2, name: 'Photography', activated: false },
      { id: 4, name: 'Illustrations', activated: false },
      { id: 3, name: 'Posters', activated: false },
    ];

    this.loadFilters();

    // URL-driven load — restores state on back/forward
    this.sub.add(this.route.queryParamMap.subscribe(qp => {
      this.currentPage = Number(qp.get('page')) || 1;
      this.selectedProductTypes = qp.get('product_type') ? qp.get('product_type')!.split(',') : [];
      this.selectedCollections   = qp.get('collection')   ? qp.get('collection')!.split(',').map(Number) : [];
      this.selectedArtTypes      = qp.get('art_type')     ? qp.get('art_type')!.split(',').map(Number) : [];
      this.selectedSpaces        = qp.get('space')        ? qp.get('space')!.split(',').map(Number) : [];
      this.selectedSizes         = qp.get('size')         ? qp.get('size')!.split(',').map(Number) : [];
      this.selectedOrientations  = qp.get('orientation')  ? qp.get('orientation')!.split(',').map(Number) : [];

      // sync activated flags on static arrays
      this.productTypes.forEach((i: any) => i.activated = this.selectedProductTypes.includes(i.slug));
      this.artTypes.forEach((i: any) => i.activated = this.selectedArtTypes.includes(i.id));

      this.loadProducts();
    }));
  }


  openSections: any = {
    material: true,
    collection: false,
    artType: false,
    space: false,
    size: false,
    orientation: false
  };

  toggleSection(key: string) {
    this.openSections[key] = !this.openSections[key];
  }

  getFacetCount(type: string, key: any): number {
    if (!this.facet) return 0;

    const group = this.facet[type];
    if (!group) return 0;

    return group[key] || 0;
  }

  loadFilters() {
    const orderBy = JSON.stringify([['name', 'asc']]);

    this.baseApiService.filter('[]', orderBy, 0, -1, 'featured_art_collection')
      .subscribe(res => {
        this.collections = res['data'];
        this.collections.forEach((i: any) => i.activated = this.selectedCollections.includes(i.id));
      });

    this.baseApiService.filter('[]', orderBy, 0, -1, 'sizes')
      .subscribe(res => {
        this.sizes = res['data'];
        this.sizes.forEach((i: any) => i.activated = this.selectedSizes.includes(i.id));
      });

    this.baseApiService.filter('[]', orderBy, 0, -1, 'orientation')
      .subscribe(res => {
        this.orientations = res['data'];
        this.orientations.forEach((i: any) => i.activated = this.selectedOrientations.includes(i.id));
      });

    this.baseApiService.filter('[]', orderBy, 0, -1, 'space')
      .subscribe(res => {
        this.spaces = res['data'];
        this.spaces.forEach((i: any) => i.activated = this.selectedSpaces.includes(i.id));
      });
  }

  private buildParams(): HttpParams {
    let params = new HttpParams();

    params = params.set('page', String(this.currentPage));
    params = params.set('images', String(this.pageSize));
    params = params.set('sortby', this.sortby);
    params = params.set('sortdir', this.sortDir);
    params = params.set('product', 'wall_art');

    if (this.selectedProductTypes.length) {
      params = params.set('product_type', this.selectedProductTypes.join(','));
    }

    if (this.selectedCollections.length) {
      params = params.set('collection', this.selectedCollections.join(','));
    }

    if (this.selectedArtTypes.length) {
      params = params.set('art_type', this.selectedArtTypes.join(','));
    }

    if (this.selectedSpaces.length) {
      params = params.set('space', this.selectedSpaces.join(','));
    }

    if (this.selectedSizes.length) {
      params = params.set('size', this.selectedSizes.join(','));
    }

    if (this.selectedOrientations.length) {
      params = params.set('orientation', this.selectedOrientations.join(','));
    }

    return params;
  }

  loadProducts() {
    this.isLoading = true;
    this.loadError = false;

    const params = this.buildParams();

    this.wallpaperService
      .getOnSaleSolr(params.toString())
      .subscribe({
        next: (res: any) => {
          this.products = res?.results || [];
          this.totalRecords = res?.total || 0;
          this.isLoading = false;
          this.cdr.detectChanges();
          this.facet = res?.facet_field || {};
        },
        error: () => {
          this.loadError = true;
          this.isLoading = false;
        }
      });
  }

  private updateSort() {
    switch (this.sortBy) {
      case 'Price: Low → High':
        this.sortby = 'net_price_tax';
        this.sortDir = 'asc';
        break;
      case 'Price: High → Low':
        this.sortby = 'net_price_tax';
        this.sortDir = 'desc';
        break;
      case 'Popular':
        this.sortby = 'popular_art';
        this.sortDir = 'desc';
        break;
      case 'Relevance':
        this.sortby = 'recommended';
        this.sortDir = 'desc';
        break;
      default:
        this.sortby = 'created_at';
        this.sortDir = 'desc';
    }
  }

  onSortChange(opt: string) {
    this.sortBy = opt;
    this.updateSort();
    this.showSortDropdown = false;
    this.currentPage = 1;
    this.loadProducts();
  }

  get totalPages(): number {
    return Math.ceil(this.totalRecords / this.pageSize);
  }

  get pageNumbers(): number[] {
    const total = this.totalPages;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

    const pages: number[] = [1];
    if (this.currentPage > 3) pages.push(-1);

    for (let i = Math.max(2, this.currentPage - 1); i <= Math.min(total - 1, this.currentPage + 1); i++) {
      pages.push(i);
    }

    if (this.currentPage < total - 2) pages.push(-1);
    pages.push(total);

    return pages;
  }

  goToPage(p: number) {
    if (p < 1 || p > this.totalPages || p === this.currentPage) return;
    this.currentPage = p;
    this.syncToUrl();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // helpers
  getItemImage(item: any): string {
    return item?.hover_thumbnail || item?.list_thumbnail || '';
  }

  getItemName(item: any): string {
    return item?.caption || 'Art Ready';
  }

  getItemSize(item: any): string {
    return item?.final_size || '';
  }

  getItemPrice(item: any): number {
    return item?.net_price_tax || 0;
  }

  getItemMrp(item: any): number {
    return item?.list_price_tax || 0;
  }

  getDiscountPercent(item: any): number {
    return item?.discount || 0;
  }

  isPopular(item: any): boolean {
    return item?.popular_art === 1;
  }

  addToCart(item: any, e: Event) {
    e.stopPropagation();
    if (!item?.id) {
      this.state.showToast('Unable to add to cart', 'info');
      return;
    }

    const user = this.authService.currentUser;
    const customerId = Number(user?.customers_id) || Number(user?.id) || 0;

    if (user && customerId > 0) {
      this.http.post(`${this.apiUrl}/api/auth/addOnSaleItemToCart`, {
        on_sale_id: item.id,
        customer_id: customerId,
        image_path: '',
        width: 1,
        height: 1,
        installation_pincode: 0,
        frame_id: 0
      }, this.authHeaders).subscribe({
        next: () => {
          this.state.showToast('Product added to cart successfully!', 'cart');
          this.state.loadCartFromApi(true);
          this.router.navigate(['/checkout']);
        },
        error: (err) => {
          console.error('addOnSaleItemToCart error:', err);
          this.state.showToast('Failed to add to cart', 'info');
        }
      });
    } else {
      this.http.post(`${this.apiUrl}/api/unauth/fillSaleItemToCart`, {
        on_sale_id: item.id,
        image_path: '',
        width: 1,
        height: 1,
        installation_pincode: 0,
        session_id: this.sessionId
      }).subscribe({
        next: () => {
          this.state.showToast('Product added to cart successfully!', 'cart');
          this.state.loadCartFromApi(true);
          this.router.navigate(['/checkout']);
        },
        error: (err) => {
          console.error('fillSaleItemToCart error:', err);
          this.state.showToast('Failed to add to cart', 'info');
        }
      });
    }
  }

  toggleWishlist(item: any, e: Event) {
    e.stopPropagation();
    const raw = item.net_price_tax || item.net_price
      || item.list_price_tax || item.list_price
      || item.price || 0;
    this.state.toggleWishlist(String(item.id), {
      id: String(item.id),
      name: item.caption || item.title || item.name || 'Art Ready',
      image: item.list_thumbnail || item.hover_thumbnail,
      price: Number(raw) || 0,
      wallpaper_type: item.wallpaper_type || 'Featured',
      slug: item.title_slug || item.slug
    });
  }

  isWishlisted(id: string): boolean {
    return this.wishlistIds.has(id);
  }

  navigateToPDP(item: any) {
    this.router.navigate(['/featured-art', item.title_slug]);
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  private syncToUrl() {
    const params: any = { page: this.currentPage };
    if (this.selectedProductTypes.length) params.product_type = this.selectedProductTypes.join(',');
    if (this.selectedCollections.length)  params.collection   = this.selectedCollections.join(',');
    if (this.selectedArtTypes.length)     params.art_type     = this.selectedArtTypes.join(',');
    if (this.selectedSpaces.length)       params.space        = this.selectedSpaces.join(',');
    if (this.selectedSizes.length)        params.size         = this.selectedSizes.join(',');
    if (this.selectedOrientations.length) params.orientation  = this.selectedOrientations.join(',');
    this.router.navigate([], { relativeTo: this.route, queryParams: params, replaceUrl: false });
  }

  toggleFilter(type: string, value: any, checked: boolean) {
    const map: any = {
      product_type: this.selectedProductTypes,
      collection: this.selectedCollections,
      art_type: this.selectedArtTypes,
      space: this.selectedSpaces,
      size: this.selectedSizes,
      orientation: this.selectedOrientations
    };
    const arr = map[type];
    if (!arr) return;
    if (checked) {
      if (!arr.includes(value)) arr.push(value);
    } else {
      const index = arr.indexOf(value);
      if (index > -1) arr.splice(index, 1);
    }
    this.currentPage = 1;
    this.syncToUrl(); // triggers queryParamMap → loadProducts
  }

  clearAll() {
    this.selectedCollections = [];
    this.selectedArtTypes = [];
    this.selectedSpaces = [];
    this.selectedSizes = [];
    this.selectedOrientations = [];
    this.selectedProductTypes = [];
    this.selectedFrameStyles = [];
    this.selectedTypeOfFrames = [];
    this.selectedDiscounts = [];
    this.collections.forEach((i: any) => i.activated = false);
    this.artTypes.forEach((i: any) => i.activated = false);
    this.spaces.forEach((i: any) => i.activated = false);
    this.sizes.forEach((i: any) => i.activated = false);
    this.orientations.forEach((i: any) => i.activated = false);
    this.productTypes.forEach((i: any) => i.activated = false);
    this.showFilterDrawer = false;
    this.currentPage = 1;
    this.syncToUrl();
  }

  applyFilters() {
    this.showFilterDrawer = false;
  }

}